import { useState, useEffect, useRef, useCallback } from "react";
import * as ort from "onnxruntime-web";

const THROTTLE_MS = 150;
const MODEL_URL = "/models/document-detect/yolov8n-doc.onnx";
const INPUT_SIZE = 640;
const CONF_THRESHOLD = 0.25;
const IOU_THRESHOLD = 0.45;

// ── Fixed thresholds (not strategy-dependent) ──
const DOC_SIZE_MAX = 0.80;
const GLARE_PIXEL_THRESHOLD = 240;
const LUMA_MEAN_MIN = 30;
const LUMA_MEAN_MAX = 230;
const LUMA_STD_MIN = 10;
const DARK_ROOM_THRESHOLD = 30;
const DOC_ASPECT_MIN = 1.2;
const DOC_ASPECT_MAX = 1.75;
const CONTOUR_MAX_AREA_RATIO = 0.85;
const CANNY_LOW = 50;
const CANNY_HIGH = 150;
const APPROX_EPSILON_RATIO = 0.02;
const CONTOUR_DOWNSCALE_W = 320;
const MANUAL_CAPTURE_TIMEOUT = 20000;

// ── Capture strategy presets ──
// "snap"    — grab and go: minimal hold time, lenient quality gates
// "steady"  — balanced default: quick but reliable
// "precise" — thorough: strict quality, longer confirmation hold
const CAPTURE_STRATEGIES = {
  snap: {
    autoCaptureDelay: 0,          // capture immediately when gates pass
    temporalFrames: 1,            // single frame confirmation
    temporalFramesBack: 1,        // back side: same
    temporalIOU: 0.35,            // loose stability requirement
    blurThreshold: 25,            // very lenient on blur
    glareAreaThreshold: 0.15,     // tolerate more glare
    centerThreshold: 0.25,        // loose centering
    minimumDocumentArea: 0.08,    // accept smaller docs in frame
    contourMinAreaRatio: 0.05,    // detect smaller rectangles
  },
  steady: {
    autoCaptureDelay: 700,        // short hold — feels responsive
    temporalFrames: 2,            // two consecutive frames
    temporalFramesBack: 1,        // back side: single frame
    temporalIOU: 0.45,            // moderate stability
    blurThreshold: 50,            // moderate blur tolerance
    glareAreaThreshold: 0.10,     // moderate glare tolerance
    centerThreshold: 0.20,        // moderate centering
    minimumDocumentArea: 0.10,    // moderate size requirement
    contourMinAreaRatio: 0.06,    // moderate contour area
  },
  precise: {
    autoCaptureDelay: 1500,       // hold still for 1.5s
    temporalFrames: 3,            // three consecutive stable frames
    temporalFramesBack: 2,        // back side: two frames
    temporalIOU: 0.60,            // strict stability
    blurThreshold: 100,           // strict blur check
    glareAreaThreshold: 0.05,     // strict glare check
    centerThreshold: 0.15,        // strict centering
    minimumDocumentArea: 0.15,    // document must fill more of frame
    contourMinAreaRatio: 0.08,    // larger contour required
  },
};

const DEFAULT_STRATEGY = "snap";

const CLASS_NAMES = ["passport", "id_card", "drivers_license", "id_card_back", "drivers_license_back"];

// Classes 3 and 4 are back-of-document detections
const BACK_CLASS_IDS = new Set([3, 4]);

const defaultGatingState = {
  documentPresent: false,
  classified: false,
  size: false,
  centering: false,
  blur: false,
  glare: false,
  lighting: false,
};

// ── Image analysis (same approach as useFaceDetection.js) ──

function analyzeLighting(video, box) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const x = Math.max(0, Math.round(box.x));
  const y = Math.max(0, Math.round(box.y));
  const w = Math.min(Math.round(box.width), video.videoWidth - x);
  const h = Math.min(Math.round(box.height), video.videoHeight - y);
  if (w <= 0 || h <= 0) return { mean: 0, stdDev: 0 };

  canvas.width = w;
  canvas.height = h;
  ctx.drawImage(video, x, y, w, h, 0, 0, w, h);
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;

  let sum = 0;
  let count = 0;
  const lumaValues = [];
  for (let i = 0; i < data.length; i += 4) {
    const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    lumaValues.push(luma);
    sum += luma;
    count++;
  }

  const mean = count > 0 ? sum / count : 0;
  let variance = 0;
  for (let i = 0; i < lumaValues.length; i++) {
    variance += (lumaValues[i] - mean) ** 2;
  }
  const stdDev = count > 0 ? Math.sqrt(variance / count) : 0;

  return { mean, stdDev };
}

function analyzeBlur(video, box) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const x = Math.max(0, Math.round(box.x));
  const y = Math.max(0, Math.round(box.y));
  const w = Math.min(Math.round(box.width), video.videoWidth - x);
  const h = Math.min(Math.round(box.height), video.videoHeight - y);
  if (w <= 0 || h <= 0) return 0;

  const sw = Math.min(w, 200);
  const sh = Math.min(h, 150);
  canvas.width = sw;
  canvas.height = sh;
  ctx.drawImage(video, x, y, w, h, 0, 0, sw, sh);
  const imageData = ctx.getImageData(0, 0, sw, sh);
  const data = imageData.data;

  const gray = new Float32Array(sw * sh);
  for (let i = 0; i < gray.length; i++) {
    const pi = i * 4;
    gray[i] = 0.299 * data[pi] + 0.587 * data[pi + 1] + 0.114 * data[pi + 2];
  }

  let lapSum = 0;
  let lapSumSq = 0;
  let lapCount = 0;
  for (let row = 1; row < sh - 1; row++) {
    for (let col = 1; col < sw - 1; col++) {
      const idx = row * sw + col;
      const lap =
        gray[idx - sw] +
        gray[idx - 1] +
        -4 * gray[idx] +
        gray[idx + 1] +
        gray[idx + sw];
      lapSum += lap;
      lapSumSq += lap * lap;
      lapCount++;
    }
  }

  if (lapCount === 0) return 0;
  const lapMean = lapSum / lapCount;
  const lapVariance = lapSumSq / lapCount - lapMean * lapMean;
  return lapVariance;
}

function analyzeGlare(video, box) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const x = Math.max(0, Math.round(box.x));
  const y = Math.max(0, Math.round(box.y));
  const w = Math.min(Math.round(box.width), video.videoWidth - x);
  const h = Math.min(Math.round(box.height), video.videoHeight - y);
  if (w <= 0 || h <= 0) return 0;

  const sw = Math.min(w, 200);
  const sh = Math.min(h, 150);
  canvas.width = sw;
  canvas.height = sh;
  ctx.drawImage(video, x, y, w, h, 0, 0, sw, sh);
  const imageData = ctx.getImageData(0, 0, sw, sh);
  const data = imageData.data;

  let brightPixels = 0;
  const totalPixels = sw * sh;
  for (let i = 0; i < data.length; i += 4) {
    const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    if (luma > GLARE_PIXEL_THRESHOLD) brightPixels++;
  }

  return totalPixels > 0 ? brightPixels / totalPixels : 0;
}

function analyzeFrameBrightness(video) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const sw = 64;
  const sh = 48;
  canvas.width = sw;
  canvas.height = sh;
  ctx.drawImage(video, 0, 0, sw, sh);
  const imageData = ctx.getImageData(0, 0, sw, sh);
  const data = imageData.data;
  let sum = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 4) {
    sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    count++;
  }
  return count > 0 ? sum / count : 0;
}

// ── Pure JS contour-based document detection ──
// Implements Canny edge detection + contour tracing + polygon approximation
// entirely in JavaScript. Zero external dependencies, instant startup.
//
// Pipeline: grayscale → Gaussian blur → Canny → contour trace → RDP simplify
//           → filter quadrilaterals by area, aspect ratio, convexity

// 5x5 Gaussian blur (sigma ~1.4)
function gaussianBlur5x5(gray, w, h) {
  const kernel = [
    1, 4, 7, 4, 1,
    4, 16, 26, 16, 4,
    7, 26, 41, 26, 7,
    4, 16, 26, 16, 4,
    1, 4, 7, 4, 1,
  ];
  const kSum = 273;
  const out = new Uint8Array(w * h);
  for (let r = 2; r < h - 2; r++) {
    for (let c = 2; c < w - 2; c++) {
      let sum = 0;
      for (let kr = -2; kr <= 2; kr++) {
        for (let kc = -2; kc <= 2; kc++) {
          sum += gray[(r + kr) * w + (c + kc)] * kernel[(kr + 2) * 5 + (kc + 2)];
        }
      }
      out[r * w + c] = (sum / kSum) | 0;
    }
  }
  return out;
}

// Canny edge detection: Sobel gradients → magnitude/direction → NMS → hysteresis
function cannyEdges(gray, w, h, low, high) {
  const mag = new Float32Array(w * h);
  const dir = new Uint8Array(w * h); // 0=horiz, 1=diag45, 2=vert, 3=diag135

  // Sobel gradients
  for (let r = 1; r < h - 1; r++) {
    for (let c = 1; c < w - 1; c++) {
      const idx = r * w + c;
      const gx =
        -gray[(r - 1) * w + (c - 1)] + gray[(r - 1) * w + (c + 1)]
        - 2 * gray[r * w + (c - 1)] + 2 * gray[r * w + (c + 1)]
        - gray[(r + 1) * w + (c - 1)] + gray[(r + 1) * w + (c + 1)];
      const gy =
        -gray[(r - 1) * w + (c - 1)] - 2 * gray[(r - 1) * w + c] - gray[(r - 1) * w + (c + 1)]
        + gray[(r + 1) * w + (c - 1)] + 2 * gray[(r + 1) * w + c] + gray[(r + 1) * w + (c + 1)];
      mag[idx] = Math.sqrt(gx * gx + gy * gy);
      // Quantize direction to 4 buckets
      let angle = Math.atan2(gy, gx) * (180 / Math.PI);
      if (angle < 0) angle += 180;
      if (angle < 22.5 || angle >= 157.5) dir[idx] = 0;      // horizontal edge
      else if (angle < 67.5) dir[idx] = 1;                     // 45° diagonal
      else if (angle < 112.5) dir[idx] = 2;                    // vertical edge
      else dir[idx] = 3;                                        // 135° diagonal
    }
  }

  // Non-maximum suppression
  const nms = new Float32Array(w * h);
  for (let r = 1; r < h - 1; r++) {
    for (let c = 1; c < w - 1; c++) {
      const idx = r * w + c;
      const m = mag[idx];
      let n1 = 0, n2 = 0;
      switch (dir[idx]) {
        case 0: n1 = mag[idx - 1]; n2 = mag[idx + 1]; break;           // compare left/right
        case 1: n1 = mag[(r - 1) * w + (c + 1)]; n2 = mag[(r + 1) * w + (c - 1)]; break;
        case 2: n1 = mag[(r - 1) * w + c]; n2 = mag[(r + 1) * w + c]; break; // compare up/down
        case 3: n1 = mag[(r - 1) * w + (c - 1)]; n2 = mag[(r + 1) * w + (c + 1)]; break;
      }
      nms[idx] = (m >= n1 && m >= n2) ? m : 0;
    }
  }

  // Hysteresis thresholding: strong (>high), weak (>low), connected weak→strong
  const edges = new Uint8Array(w * h); // 0=off, 255=edge
  const STRONG = 2, WEAK = 1;
  const labels = new Uint8Array(w * h);
  for (let i = 0; i < nms.length; i++) {
    if (nms[i] >= high) labels[i] = STRONG;
    else if (nms[i] >= low) labels[i] = WEAK;
  }

  // BFS: promote weak pixels connected to strong pixels
  const stack = [];
  for (let i = 0; i < labels.length; i++) {
    if (labels[i] === STRONG) {
      edges[i] = 255;
      stack.push(i);
    }
  }
  while (stack.length > 0) {
    const idx = stack.pop();
    const r = (idx / w) | 0;
    const c = idx % w;
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        const nr = r + dr, nc = c + dc;
        if (nr < 0 || nr >= h || nc < 0 || nc >= w) continue;
        const ni = nr * w + nc;
        if (labels[ni] === WEAK) {
          labels[ni] = STRONG;
          edges[ni] = 255;
          stack.push(ni);
        }
      }
    }
  }

  return edges;
}

// Dilate binary edge image (3x3) to close small gaps between edge fragments.
// This is critical — Canny edges often have 1-2 pixel gaps at corners or
// where contrast changes. Dilation connects nearby edge fragments so they
// can be traced as one continuous contour.
function dilate3x3(edges, w, h) {
  const out = new Uint8Array(w * h);
  for (let r = 1; r < h - 1; r++) {
    for (let c = 1; c < w - 1; c++) {
      if (edges[r * w + c] === 255) {
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            out[(r + dr) * w + (c + dc)] = 255;
          }
        }
      }
    }
  }
  return out;
}

// Edge chain following on a binary edge image (NOT Moore boundary tracing).
// Follows connected edge pixels along thin 1-pixel-wide Canny edges.
// Returns an array of contours, each contour is an array of {x, y} points.
function traceContours(edges, w, h) {
  const visited = new Uint8Array(w * h);
  const contours = [];
  // 8-connected neighbor offsets (clockwise from right)
  const dx = [1, 1, 0, -1, -1, -1, 0, 1];
  const dy = [0, 1, 1, 1, 0, -1, -1, -1];

  for (let r = 1; r < h - 1; r++) {
    for (let c = 1; c < w - 1; c++) {
      const idx = r * w + c;
      if (edges[idx] !== 255 || visited[idx]) continue;

      // Follow connected edge pixels in a chain
      const contour = [];
      let cr = r, cc = c;
      let prevDir = -1;
      const maxSteps = w * h;

      for (let steps = 0; steps < maxSteps; steps++) {
        contour.push({ x: cc, y: cr });
        visited[cr * w + cc] = 1;

        // Search 8 neighbors for the next unvisited edge pixel.
        // Start search from a direction that continues forward
        // (opposite of where we came from, offset by -2 to prefer straight ahead)
        let found = false;
        const searchStart = prevDir >= 0 ? (prevDir + 5) % 8 : 0;
        for (let i = 0; i < 8; i++) {
          const d = (searchStart + i) % 8;
          const nr = cr + dy[d];
          const nc = cc + dx[d];
          if (nr >= 1 && nr < h - 1 && nc >= 1 && nc < w - 1 &&
              edges[nr * w + nc] === 255 && !visited[nr * w + nc]) {
            prevDir = d;
            cr = nr;
            cc = nc;
            found = true;
            break;
          }
        }

        if (!found) break;

        // Check if we've returned near the start (closed contour)
        if (contour.length > 10 && Math.abs(cr - r) <= 1 && Math.abs(cc - c) <= 1) {
          break;
        }
      }

      // Only keep contours with enough points to form a shape
      if (contour.length >= 30) {
        contours.push(contour);
      }
    }
  }

  return contours;
}

// Ramer-Douglas-Peucker polygon approximation
function rdpSimplify(points, epsilon) {
  if (points.length <= 2) return points;

  // Find the point with the maximum distance from the line (first→last)
  const first = points[0];
  const last = points[points.length - 1];
  let maxDist = 0;
  let maxIdx = 0;

  const lineLen = Math.sqrt((last.x - first.x) ** 2 + (last.y - first.y) ** 2);

  for (let i = 1; i < points.length - 1; i++) {
    let dist;
    if (lineLen < 1e-10) {
      dist = Math.sqrt((points[i].x - first.x) ** 2 + (points[i].y - first.y) ** 2);
    } else {
      // Perpendicular distance from point to line
      dist = Math.abs(
        (last.y - first.y) * points[i].x -
        (last.x - first.x) * points[i].y +
        last.x * first.y -
        last.y * first.x
      ) / lineLen;
    }
    if (dist > maxDist) {
      maxDist = dist;
      maxIdx = i;
    }
  }

  if (maxDist > epsilon) {
    const left = rdpSimplify(points.slice(0, maxIdx + 1), epsilon);
    const right = rdpSimplify(points.slice(maxIdx), epsilon);
    return left.slice(0, -1).concat(right);
  }

  return [first, last];
}

// Approximate a closed contour to a polygon (like OpenCV's approxPolyDP)
function approxPoly(contour, epsilonRatio) {
  // Compute perimeter
  let perimeter = 0;
  for (let i = 0; i < contour.length; i++) {
    const j = (i + 1) % contour.length;
    perimeter += Math.sqrt(
      (contour[j].x - contour[i].x) ** 2 + (contour[j].y - contour[i].y) ** 2
    );
  }
  const epsilon = epsilonRatio * perimeter;

  // For closed contours, find the point furthest from the centroid as start,
  // then run RDP on the linearized contour
  const simplified = rdpSimplify(contour, epsilon);

  // Remove near-duplicate last point if it's the same as first (closed loop)
  if (simplified.length > 1) {
    const f = simplified[0];
    const l = simplified[simplified.length - 1];
    if (Math.abs(f.x - l.x) < 3 && Math.abs(f.y - l.y) < 3) {
      simplified.pop();
    }
  }

  return simplified;
}

// Compute polygon area using the shoelace formula
function polygonArea(pts) {
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    area += pts[i].x * pts[j].y;
    area -= pts[j].x * pts[i].y;
  }
  return Math.abs(area) / 2;
}

// Check if a polygon is convex using cross products
function isConvex(pts) {
  if (pts.length < 3) return false;
  let sign = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const c = pts[(i + 2) % pts.length];
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (cross !== 0) {
      if (sign === 0) sign = cross > 0 ? 1 : -1;
      else if ((cross > 0 ? 1 : -1) !== sign) return false;
    }
  }
  return true;
}

// Compute aspect ratio from 4 corner points
function quadAspectRatio(pts) {
  // Measure average of two pairs of opposite sides
  const d01 = Math.sqrt((pts[1].x - pts[0].x) ** 2 + (pts[1].y - pts[0].y) ** 2);
  const d12 = Math.sqrt((pts[2].x - pts[1].x) ** 2 + (pts[2].y - pts[1].y) ** 2);
  const d23 = Math.sqrt((pts[3].x - pts[2].x) ** 2 + (pts[3].y - pts[2].y) ** 2);
  const d30 = Math.sqrt((pts[0].x - pts[3].x) ** 2 + (pts[0].y - pts[3].y) ** 2);
  const sideA = (d01 + d23) / 2; // average of opposite sides
  const sideB = (d12 + d30) / 2;
  const long = Math.max(sideA, sideB);
  const short = Math.min(sideA, sideB);
  return short > 0 ? long / short : 0;
}

// Main contour-based document detection (pure JS, no dependencies)
// backSide relaxes aspect ratio constraints since backs often have barcodes/magstripes
function analyzeDocumentContours(video, frameW, frameH, backSide = false, contourMinArea = 0.06) {
  const noResult = {
    detected: false,
    contourFound: false,
    aspectOk: false,
    vertices: 0,
    contourArea: 0,
    contourAreaRatio: 0,
    aspectRatio: 0,
    boundingBox: null,
    corners: null,
  };

  try {
    // Downscale for performance — process at 320px wide
    const scale = Math.min(1, CONTOUR_DOWNSCALE_W / frameW);
    const sw = Math.round(frameW * scale);
    const sh = Math.round(frameH * scale);

    const canvas = document.createElement("canvas");
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, sw, sh);
    const imageData = ctx.getImageData(0, 0, sw, sh);
    const data = imageData.data;

    // Grayscale
    const gray = new Uint8Array(sw * sh);
    for (let i = 0; i < gray.length; i++) {
      const pi = i * 4;
      gray[i] = (0.299 * data[pi] + 0.587 * data[pi + 1] + 0.114 * data[pi + 2]) | 0;
    }

    // Gaussian blur
    const blurred = gaussianBlur5x5(gray, sw, sh);

    // Canny edge detection
    const rawEdges = cannyEdges(blurred, sw, sh, CANNY_LOW, CANNY_HIGH);

    // Count edge pixels for debug
    let edgePixelCount = 0;
    for (let i = 0; i < rawEdges.length; i++) {
      if (rawEdges[i] === 255) edgePixelCount++;
    }

    // Dilate edges to close small gaps (critical for connecting edge fragments)
    const edges = dilate3x3(rawEdges, sw, sh);

    // Trace contours using edge chain following
    const contours = traceContours(edges, sw, sh);

    const frameArea = sw * sh;
    let bestPoly = null;
    let bestArea = 0;
    let bestAspect = 0;

    // Track rejection reasons for debug
    let rejectArea = 0, rejectAspect = 0, rejectConvex = 0, totalQuads = 0;

    for (const contour of contours) {
      // Approximate to polygon
      const poly = approxPoly(contour, APPROX_EPSILON_RATIO);

      // Want a quadrilateral (allow 4-6 vertices, then recheck)
      if (poly.length < 4 || poly.length > 6) continue;

      // For 5-6 vertex polygons, try removing the shortest edge to get a quad
      let quad = poly;
      if (poly.length > 4) {
        // Find and remove the vertex that contributes the least area change
        let minDist = Infinity;
        let minIdx = 0;
        for (let i = 0; i < poly.length; i++) {
          const prev = poly[(i - 1 + poly.length) % poly.length];
          const next = poly[(i + 1) % poly.length];
          const dist = Math.sqrt((next.x - prev.x) ** 2 + (next.y - prev.y) ** 2);
          if (dist < minDist) {
            minDist = dist;
            minIdx = i;
          }
        }
        quad = [...poly];
        quad.splice(minIdx, 1);
        if (quad.length > 4) continue; // still too many vertices
      }

      totalQuads++;

      // Check area
      const area = polygonArea(quad);
      const areaRatio = area / frameArea;
      if (areaRatio < contourMinArea || areaRatio > CONTOUR_MAX_AREA_RATIO) {
        rejectArea++;
        continue;
      }

      // Check aspect ratio (wider range for back side — barcodes/magstripes
      // can make the detected rectangle appear more square or wider)
      const aspect = quadAspectRatio(quad);
      const aspectMin = backSide ? 1.0 : DOC_ASPECT_MIN;
      const aspectMax = backSide ? 2.2 : DOC_ASPECT_MAX;
      if (aspect < aspectMin || aspect > aspectMax) {
        rejectAspect++;
        continue;
      }

      // Check convexity (skip for back side — edges are less clean)
      if (!backSide && !isConvex(quad)) {
        rejectConvex++;
        continue;
      }

      // Keep the largest valid quadrilateral
      if (area > bestArea) {
        bestPoly = quad;
        bestArea = area;
        bestAspect = aspect;
      }
    }

    const debugInfo = {
      edgePixels: edgePixelCount,
      edgePercent: ((edgePixelCount / (sw * sh)) * 100).toFixed(1),
      contoursScanned: contours.length,
      contourLengths: contours.slice(0, 5).map((c) => c.length),
      quadsFound: totalQuads,
      rejectArea,
      rejectAspect,
      rejectConvex,
      frameSize: `${sw}x${sh}`,
    };

    if (bestPoly) {
      // Scale corners back to original frame coordinates
      const corners = bestPoly.map((p) => ({
        x: Math.round(p.x / scale),
        y: Math.round(p.y / scale),
      }));

      const xs = corners.map((c) => c.x);
      const ys = corners.map((c) => c.y);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      const maxX = Math.max(...xs);
      const maxY = Math.max(...ys);

      const areaRatio = bestArea / frameArea;
      const minAreaNeeded = backSide ? contourMinArea * 0.75 : contourMinArea;

      return {
        detected: areaRatio >= minAreaNeeded,
        contourFound: true,
        aspectOk: true,
        vertices: 4,
        contourArea: bestArea,
        contourAreaRatio: areaRatio,
        aspectRatio: bestAspect,
        boundingBox: {
          x: minX,
          y: minY,
          width: maxX - minX,
          height: maxY - minY,
        },
        corners,
        ...debugInfo,
      };
    }

    return { ...noResult, ...debugInfo };
  } catch (err) {
    console.warn("[DocDetect] Contour analysis error:", err.message);
    return noResult;
  }
}

// ── YOLO preprocessing ──

function preprocessFrame(video, canvas, ctx) {
  canvas.width = INPUT_SIZE;
  canvas.height = INPUT_SIZE;
  ctx.drawImage(video, 0, 0, INPUT_SIZE, INPUT_SIZE);
  const imageData = ctx.getImageData(0, 0, INPUT_SIZE, INPUT_SIZE);
  const data = imageData.data;
  const float32Data = new Float32Array(3 * INPUT_SIZE * INPUT_SIZE);
  const pixelCount = INPUT_SIZE * INPUT_SIZE;

  // NHWC → NCHW, normalize to 0-1
  for (let i = 0; i < pixelCount; i++) {
    const pi = i * 4;
    float32Data[i] = data[pi] / 255.0;                          // R channel
    float32Data[pixelCount + i] = data[pi + 1] / 255.0;         // G channel
    float32Data[2 * pixelCount + i] = data[pi + 2] / 255.0;     // B channel
  }

  return new ort.Tensor("float32", float32Data, [1, 3, INPUT_SIZE, INPUT_SIZE]);
}

// ── YOLO postprocessing ──

function iou(a, b) {
  const x1 = Math.max(a.x, b.x);
  const y1 = Math.max(a.y, b.y);
  const x2 = Math.min(a.x + a.width, b.x + b.width);
  const y2 = Math.min(a.y + a.height, b.y + b.height);
  const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const union = a.width * a.height + b.width * b.height - inter;
  return union > 0 ? inter / union : 0;
}

function postprocessYolo(output, videoWidth, videoHeight) {
  const data = output.data;
  const [, numFields, numDetections] = output.dims; // [1, 4+numClasses, 8400]
  const numClasses = numFields - 4;

  const scaleX = videoWidth / INPUT_SIZE;
  const scaleY = videoHeight / INPUT_SIZE;
  const candidates = [];

  // Track peak score per class across ALL 8400 candidates (for debug)
  const peakScores = new Array(numClasses).fill(0);

  for (let i = 0; i < numDetections; i++) {
    const cx = data[0 * numDetections + i];
    const cy = data[1 * numDetections + i];
    const w = data[2 * numDetections + i];
    const h = data[3 * numDetections + i];

    let maxScore = 0;
    let classId = -1;
    for (let c = 0; c < numClasses; c++) {
      const score = data[(4 + c) * numDetections + i];
      if (score > peakScores[c]) peakScores[c] = score;
      if (score > maxScore) {
        maxScore = score;
        classId = c;
      }
    }

    if (maxScore >= CONF_THRESHOLD) {
      candidates.push({
        x: (cx - w / 2) * scaleX,
        y: (cy - h / 2) * scaleY,
        width: w * scaleX,
        height: h * scaleY,
        classId,
        className: CLASS_NAMES[classId] || "unknown",
        confidence: maxScore,
      });
    }
  }

  candidates.sort((a, b) => b.confidence - a.confidence);
  const kept = [];
  const suppressed = new Set();

  for (let i = 0; i < candidates.length; i++) {
    if (suppressed.has(i)) continue;
    kept.push(candidates[i]);
    for (let j = i + 1; j < candidates.length; j++) {
      if (!suppressed.has(j) && iou(candidates[i], candidates[j]) > IOU_THRESHOLD) {
        suppressed.add(j);
      }
    }
  }

  kept.peakScores = peakScores.map((s, i) => ({ cls: CLASS_NAMES[i] || `c${i}`, peak: s }));
  return kept;
}

// ── Feedback mapping ──

function getFeedbackKey(gating, isTooDark, areaRatio, minArea, maxArea) {
  if (!gating.documentPresent) return isTooDark ? "tooDark" : "noDocument";
  if (!gating.size) {
    if (areaRatio < minArea) return "moveCloser";
    if (areaRatio > maxArea) return "moveFurther";
    return "moveCloser";
  }
  if (!gating.centering) return "centerDocument";
  if (!gating.blur) return "tooBlurry";
  if (!gating.glare) return "reduceGlare";
  if (!gating.lighting) return isTooDark ? "tooDark" : "tooBright";
  return "holdStill";
}

// ── Main hook ──

export default function useDocumentDetection(videoRef, active, captureSettings = {}, side = "front") {
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [modelLoadError, setModelLoadError] = useState(null);
  const [gatingState, setGatingState] = useState(defaultGatingState);
  const [feedbackKey, setFeedbackKey] = useState("positionFront");
  const [isAllPassing, setIsAllPassing] = useState(false);
  const [isTooDark, setIsTooDark] = useState(false);
  const [documentType, setDocumentType] = useState(null);
  const [confidence, setConfidence] = useState(0);
  const [boundingBox, setBoundingBox] = useState(null);
  const [showManualCapture, setShowManualCapture] = useState(false);
  const [docPresenceDetail, setDocPresenceDetail] = useState(null);
  const [yoloDetail, setYoloDetail] = useState(null);

  const sessionRef = useRef(null);
  const allPassingStartRef = useRef(null);
  const rafRef = useRef(null);
  const lastDetectRef = useRef(0);
  const detectionStartRef = useRef(null);
  const onAutoCaptureRef = useRef(null);
  const preprocessCanvasRef = useRef(null);
  const preprocessCtxRef = useRef(null);
  const areaRatioRef = useRef(0);

  // Solution 5: Temporal consistency refs
  const consecutiveDetectionsRef = useRef(0);
  const lastBoundingBoxRef = useRef(null);
  const docPresenceDetailRef = useRef(null);

  // Feedback debounce: prevent flickering messages
  const lastFeedbackKeyRef = useRef(null);
  const feedbackStableCountRef = useRef(0);
  const FEEDBACK_STABLE_FRAMES = 3; // new message must persist 3 frames before showing

  // Back-side fallback: if no contour is found but quality gates pass on the
  // guide box area, count consecutive quality-passing frames and accept after
  // a threshold (back sides often lack clean rectangular edges)
  const backQualityPassCountRef = useRef(0);
  const BACK_QUALITY_FRAMES_NEEDED = 5; // ~750ms at 150ms throttle

  // Frame chain for capture integrity
  const frameChainRef = useRef([]);

  // Orientation telemetry — records device orientation during detection loop.
  // Real phones produce varying readings; scripted attacks produce none.
  const orientationRef = useRef([]);
  const orientationStartRef = useRef(null);

  // Resolve capture strategy
  const strategyName = captureSettings?.captureStrategy || DEFAULT_STRATEGY;
  const strategy = CAPTURE_STRATEGIES[strategyName] || CAPTURE_STRATEGIES[DEFAULT_STRATEGY];

  // Merge settings: strategy defaults → captureSettings overrides
  const settings = {
    blurPolicy: "enabled",
    glarePolicy: "enabled",
    autoCaptureDelay: strategy.autoCaptureDelay,
    minimumDocumentArea: strategy.minimumDocumentArea,
    maximumDocumentArea: DOC_SIZE_MAX,
    temporalFrames: strategy.temporalFrames,
    temporalFramesBack: strategy.temporalFramesBack,
    temporalIOU: strategy.temporalIOU,
    blurThreshold: strategy.blurThreshold,
    glareAreaThreshold: strategy.glareAreaThreshold,
    centerThreshold: strategy.centerThreshold,
    contourMinAreaRatio: strategy.contourMinAreaRatio,
    captureStrategy: strategyName,
    ...captureSettings,
    lightingThresholds: {
      tooDarkThreshold: LUMA_MEAN_MIN,
      tooBrightThreshold: LUMA_MEAN_MAX,
      ...captureSettings?.lightingThresholds,
    },
  };

  // Load ONNX model
  useEffect(() => {
    let cancelled = false;

    async function loadModel() {
      try {
        console.log("[DocDetect] Loading YOLO model from", MODEL_URL);

        ort.env.logLevel = "error"; // suppress CPU vendor warnings
        ort.env.wasm.wasmPaths = "/";
        ort.env.wasm.numThreads = 1;
        ort.env.wasm.proxy = false;

        const session = await ort.InferenceSession.create(MODEL_URL, {
          executionProviders: ["wasm"],
          graphOptimizationLevel: "all",
        });

        if (!cancelled) {
          sessionRef.current = session;
          setModelsLoaded(true);
          console.log("[DocDetect] YOLO model loaded successfully");
        } else {
          session.release();
        }
      } catch (err) {
        console.warn("[DocDetect] Failed to load YOLO model, using JS contour fallback:", err.message);
        if (!cancelled) {
          setModelLoadError(err);
          setModelsLoaded(true);
        }
      }
    }

    preprocessCanvasRef.current = document.createElement("canvas");
    preprocessCtxRef.current = preprocessCanvasRef.current.getContext("2d");

    loadModel();

    return () => {
      cancelled = true;
      if (sessionRef.current) {
        sessionRef.current.release();
        sessionRef.current = null;
      }
    };
  }, []);

  const setOnAutoCapture = useCallback((fn) => {
    onAutoCaptureRef.current = fn;
  }, []);

  const getFrameChain = useCallback(() => [...frameChainRef.current], []);

  // Detection loop
  useEffect(() => {
    if (!modelsLoaded || !active) return;

    const video = videoRef?.current?.video || videoRef?.current;
    if (!video) return;

    const isBack = side === "back";
    console.log(`[DocDetect] Detection loop starting (${isBack ? "back — relaxed" : "front — strict"} mode)`);
    let running = true;
    let loopCount = 0;
    let manualCaptureShown = false;
    detectionStartRef.current = performance.now();

    // Reset state on loop start (important when switching front→back)
    consecutiveDetectionsRef.current = 0;
    lastBoundingBoxRef.current = null;
    allPassingStartRef.current = null;
    backQualityPassCountRef.current = 0;
    feedbackStableCountRef.current = 0;
    lastFeedbackKeyRef.current = null;
    frameChainRef.current = [];
    orientationRef.current = [];
    orientationStartRef.current = performance.now();
    setShowManualCapture(false);
    setFeedbackKey(isBack ? "positionBack" : "positionFront");

    // Orientation telemetry — collect device orientation samples during detection
    const orientationHandler = (e) => {
      if (orientationRef.current.length >= 30) return; // cap samples
      orientationRef.current.push({
        t: Math.round(performance.now() - (orientationStartRef.current || 0)),
        a: Math.round((e.alpha || 0) * 10) / 10,
        b: Math.round((e.beta || 0) * 10) / 10,
        g: Math.round((e.gamma || 0) * 10) / 10,
      });
    };
    window.addEventListener("deviceorientation", orientationHandler);

    const detect = async () => {
      if (!running) return;

      const now = performance.now();
      if (now - lastDetectRef.current < THROTTLE_MS) {
        rafRef.current = requestAnimationFrame(detect);
        return;
      }
      lastDetectRef.current = now;

      if (video.readyState < 2) {
        rafRef.current = requestAnimationFrame(detect);
        return;
      }

      try {
        const frameH = video.videoHeight;
        const frameW = video.videoWidth;
        const frameArea = frameW * frameH;
        const newGating = { ...defaultGatingState };

        const frameBrightness = analyzeFrameBrightness(video);
        const tooDark = frameBrightness < DARK_ROOM_THRESHOLD;

        let detectedBox = null;
        let detectedType = null;
        let detectedConfidence = 0;
        let rawDetected = false;

        // Run YOLO if model is available
        if (sessionRef.current) {
          const inputTensor = preprocessFrame(
            video,
            preprocessCanvasRef.current,
            preprocessCtxRef.current
          );

          const feeds = {};
          const inputName = sessionRef.current.inputNames[0];
          feeds[inputName] = inputTensor;
          const results = await sessionRef.current.run(feeds);
          const outputName = sessionRef.current.outputNames[0];
          const output = results[outputName];

          const detections = postprocessYolo(output, frameW, frameH);

          // Expose raw YOLO output for debug overlay
          setYoloDetail({
            source: "yolo",
            totalDetections: detections.length,
            all: detections.slice(0, 5).map(d => ({
              cls: d.className,
              conf: d.confidence,
              box: { x: Math.round(d.x), y: Math.round(d.y), w: Math.round(d.width), h: Math.round(d.height) },
            })),
            peaks: detections.peakScores || [],
            dims: `${output.dims.join("x")}`,
          });

          if (detections.length > 0) {
            const best = detections[0];
            const isBackClass = BACK_CLASS_IDS.has(best.classId);

            detectedBox = {
              x: best.x,
              y: best.y,
              width: best.width,
              height: best.height,
            };
            // Normalize back-class names to their front-side equivalents
            // (e.g. "id_card_back" → "id_card") for downstream consistency
            if (isBackClass) {
              detectedType = best.classId === 3 ? "id_card" : "drivers_license";
            } else {
              detectedType = best.className;
            }
            detectedConfidence = best.confidence;
            rawDetected = true;
          }
        } else {
          // No YOLO model — use pure JS contour detection (Canny + contour
          // tracing + polygon approximation). No external dependencies needed.
          setYoloDetail({ source: "contour", totalDetections: 0, all: [], dims: "n/a" });
          const contourResult = analyzeDocumentContours(video, frameW, frameH, isBack, settings.contourMinAreaRatio);
          docPresenceDetailRef.current = contourResult;
          setDocPresenceDetail(contourResult);

          if (contourResult.detected && contourResult.boundingBox) {
            detectedBox = contourResult.boundingBox;
            rawDetected = true;
          } else {
            // No rectangle found — use guide box for quality feedback
            const guideW = frameW * 0.7;
            const guideH = guideW / 1.586;
            detectedBox = {
              x: (frameW - guideW) / 2,
              y: (frameH - guideH) / 2,
              width: guideW,
              height: guideH,
            };
            rawDetected = false;
          }
        }

        // ── Solution 5: Temporal consistency ──
        // Require multiple consecutive frames with stable detection before confirming
        if (rawDetected && detectedBox) {
          const prevBox = lastBoundingBoxRef.current;
          if (prevBox) {
            const overlap = iou(detectedBox, prevBox);
            if (overlap >= settings.temporalIOU) {
              consecutiveDetectionsRef.current++;
            } else {
              // Box jumped — reset counter (likely noise)
              consecutiveDetectionsRef.current = 1;
            }
          } else {
            consecutiveDetectionsRef.current = 1;
          }
          lastBoundingBoxRef.current = detectedBox;

          // Only confirm presence after N consecutive stable frames
          const framesNeeded = isBack ? settings.temporalFramesBack : settings.temporalFrames;
          const temporallyConfirmed =
            consecutiveDetectionsRef.current >= framesNeeded;
          newGating.documentPresent = temporallyConfirmed;
          newGating.classified = temporallyConfirmed && (detectedType != null || !sessionRef.current);
        } else {
          // No detection this frame — decay the counter
          consecutiveDetectionsRef.current = Math.max(0, consecutiveDetectionsRef.current - 1);
          lastBoundingBoxRef.current = null;
        }

        if (detectedBox) {
          // Gate: Document size
          const docArea = detectedBox.width * detectedBox.height;
          const areaRatio = docArea / frameArea;
          areaRatioRef.current = areaRatio;
          newGating.size =
            areaRatio >= settings.minimumDocumentArea &&
            areaRatio <= settings.maximumDocumentArea;

          // Gate: Centering
          const docCenterX = detectedBox.x + detectedBox.width / 2;
          const docCenterY = detectedBox.y + detectedBox.height / 2;
          const xOff = Math.abs(docCenterX - frameW / 2) / frameW;
          const yOff = Math.abs(docCenterY - frameH / 2) / frameH;
          newGating.centering =
            xOff <= settings.centerThreshold && yOff <= settings.centerThreshold;

          // Gate: Blur
          if (settings.blurPolicy === "disabled") {
            newGating.blur = true;
          } else {
            const blurVariance = analyzeBlur(video, detectedBox);
            newGating.blur = blurVariance > settings.blurThreshold;
          }

          // Gate: Glare
          if (settings.glarePolicy === "disabled") {
            newGating.glare = true;
          } else {
            const glareRatio = analyzeGlare(video, detectedBox);
            newGating.glare = glareRatio < settings.glareAreaThreshold;
          }

          // Gate: Lighting
          const { mean, stdDev } = analyzeLighting(video, detectedBox);
          newGating.lighting =
            mean >= settings.lightingThresholds.tooDarkThreshold &&
            mean <= settings.lightingThresholds.tooBrightThreshold &&
            stdDev >= LUMA_STD_MIN;
        }

        // ── Back-side quality-gate fallback ──
        // Back sides often lack clean rectangular edges (barcodes, magstripes,
        // plain text). If we can't find a contour but the quality gates (blur,
        // glare, lighting) all pass on the guide box for enough consecutive
        // frames, treat the document as present and allow auto-capture.
        if (isBack && !newGating.documentPresent) {
          const qualityOk = newGating.blur && newGating.glare && newGating.lighting && newGating.size;
          if (qualityOk) {
            backQualityPassCountRef.current++;
            if (backQualityPassCountRef.current >= BACK_QUALITY_FRAMES_NEEDED) {
              newGating.documentPresent = true;
              newGating.classified = true;
              newGating.centering = true; // guide box is always centered
            }
          } else {
            backQualityPassCountRef.current = 0;
          }
        }

        if (loopCount % 10 === 0) {
          const detail = docPresenceDetailRef.current;
          const contourInfo = detail?.contourFound
            ? `contour: area=${(detail.contourAreaRatio * 100).toFixed(1)}% aspect=${detail.aspectRatio?.toFixed(2)} verts=${detail.vertices}`
            : `no rectangle (${detail?.contoursScanned ?? 0} contours scanned)`;
          console.log("[DocDetect] Gates:", {
            documentPresent: `${newGating.documentPresent} (streak: ${consecutiveDetectionsRef.current}/${isBack ? settings.temporalFramesBack : settings.temporalFrames}, side: ${side}, strategy: ${settings.captureStrategy})`,
            detection: contourInfo,
            classified: `${newGating.classified} (${detectedType || "none"} @ ${(detectedConfidence * 100).toFixed(1)}%)`,
            size: newGating.size,
            centering: newGating.centering,
            blur: newGating.blur,
            glare: newGating.glare,
            lighting: newGating.lighting,
          });
        }

        // Record frame chain entry for capture integrity
        frameChainRef.current.push({
          t: Math.round(now),
          d: rawDetected ? 1 : 0,
          g: Object.values(newGating).filter(Boolean).length,
        });
        if (frameChainRef.current.length > 200) frameChainRef.current.shift();

        const allPassing = Object.values(newGating).every(Boolean);

        setGatingState(newGating);
        setIsTooDark(tooDark);

        // Debounce feedback: only update the displayed message after the new
        // key has been stable for FEEDBACK_STABLE_FRAMES consecutive frames.
        // "holdStill" always shows immediately (positive reinforcement).
        const newKey = getFeedbackKey(newGating, tooDark, areaRatioRef.current, settings.minimumDocumentArea, settings.maximumDocumentArea);
        if (newKey === lastFeedbackKeyRef.current) {
          feedbackStableCountRef.current++;
        } else {
          lastFeedbackKeyRef.current = newKey;
          feedbackStableCountRef.current = 1;
        }
        if (newKey === "holdStill" || feedbackStableCountRef.current >= FEEDBACK_STABLE_FRAMES) {
          setFeedbackKey(newKey);
        }

        setIsAllPassing(allPassing);
        setDocumentType(detectedType);
        setConfidence(detectedConfidence);
        setBoundingBox(detectedBox);

        // Auto-capture state machine
        if (allPassing) {
          if (!allPassingStartRef.current) {
            allPassingStartRef.current = performance.now();
            console.log("[DocDetect] All gates PASSING — starting timer");
          } else {
            const elapsed = performance.now() - allPassingStartRef.current;
            if (elapsed >= settings.autoCaptureDelay) {
              console.log("[DocDetect] Auto-capture triggered!");
              if (onAutoCaptureRef.current) {
                running = false;
                onAutoCaptureRef.current();
                return;
              }
            }
          }
        } else {
          if (allPassingStartRef.current) {
            console.log("[DocDetect] Gates broke — timer reset");
          }
          allPassingStartRef.current = null;
        }

        // Show manual capture button after timeout
        if (!manualCaptureShown && detectionStartRef.current) {
          const elapsed = performance.now() - detectionStartRef.current;
          if (elapsed >= MANUAL_CAPTURE_TIMEOUT) {
            manualCaptureShown = true;
            setShowManualCapture(true);
            console.log("[DocDetect] Manual capture fallback enabled (20s timeout)");
          }
        }
      } catch (err) {
        console.error("[DocDetect] Detection error:", err);
      }

      loopCount++;
      if (running) {
        rafRef.current = requestAnimationFrame(detect);
      }
    };

    rafRef.current = requestAnimationFrame(detect);

    return () => {
      running = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      window.removeEventListener("deviceorientation", orientationHandler);
    };
  }, [modelsLoaded, active, videoRef, side]);

  const getOrientationSamples = useCallback(() => [...orientationRef.current], []);

  return {
    modelsLoaded,
    modelLoadError,
    gatingState,
    feedbackKey,
    isAllPassing,
    isTooDark,
    documentType,
    confidence,
    boundingBox,
    showManualCapture,
    setOnAutoCapture,
    getFrameChain,
    getOrientationSamples,
    areaRatio: areaRatioRef.current,
    docPresenceDetail,
    yoloDetail,
    captureStrategy: settings.captureStrategy,
  };
}
